import { GetTopicResponse, ImageUpload, MapRenderer, MapSetUpOptions, TopicSearchResult } from '../types';
import { HTMLTemplateResult, html } from 'lit';

import { calculateScreenshotHash } from './urls';
import { createTopicRequestFromTopic } from './api/topics-api';
import { detectMobileViewport } from './device';
import { getNewUiUrl } from './auth-cookie';
import { isDefined } from './utils';
import topicData from './state/topic-data';

/**
 * Picks the URL Thum.io should screenshot for `topic`. Mirrors the server-side
 * `thumio_target_for` in `web/utilities.rs` so both sides agree on the cache
 * key. Legacy `Google` topics keep using `standalone_map_link` (renders in the
 * Lit viewer); `MapLibre` topics target the new-app embed so the preview
 * reflects the actual new-UI rendering.
 *
 * Returns null when there's no screenshotable URL — callers fall back to the
 * `/images/share.png` placeholder.
 */
function thumioTargetForTopic(topic: GetTopicResponse | TopicSearchResult): string | null {
    if (topic.map_renderer === MapRenderer.MapLibre) {
        return `${getNewUiUrl()}/embed/screenshot/${topic._id.$oid}`;
    }
    return topic.standalone_map_link ?? null;
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result?.toString() ?? '');
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function convertToCSV(data: string[]): string {
    const csvContent = data.join(',');
    return csvContent;
}

export function downloadCSV(data: string[], filename: string): void {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export function saveAs(blob: Blob, fileName: string) {
    // does the same as FileSaver.js
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';

    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
}

export function rotateImage(imageUpload: ImageUpload): ImageUpload {
    let rotateValue = 0;
    const existingRotation = imageUpload?.rotate_value ?? 0;

    // Assume default orientation of `1`.
    if (imageUpload?.orientation === undefined) {
        imageUpload.orientation = 1;
    }

    if (imageUpload.orientation === 1) {
        imageUpload.orientation = 6;
        rotateValue = existingRotation + 90;
    } else if (imageUpload.orientation === 6) {
        imageUpload.orientation = 3;
        rotateValue = existingRotation + 90;
    } else if (imageUpload.orientation === 3) {
        imageUpload.orientation = 8;
        rotateValue = existingRotation + 90;
    } else if (imageUpload.orientation === 8) {
        imageUpload.orientation = 1;
        rotateValue = existingRotation + 90;
    } else {
        imageUpload.orientation = 1;
    }

    imageUpload.rotate_value = rotateValue;

    return imageUpload ;
}

export function getSocialMediaImageForTopic(topic: GetTopicResponse, options?: MapSetUpOptions): string {
    const target = thumioTargetForTopic(topic);
    if (target === null) {
        return '/images/share.png';
    }
    const hash = calculateScreenshotHash(topic);
    let qs = '';
    if (options) {
        if (isDefined(options.center_lat)) {
            qs += `&lat=${options.center_lat}`;
        }
        if (isDefined(options.center_lng)) {
            qs += `&lng=${options.center_lng}`;
        }
        if (isDefined(options.zoom)) {
            qs += `&zoom=${options.zoom}`;
        }
        if (isDefined(options.selectedCategories) && options.selectedCategories.length > 0) {
            const urlEncodedCategories = options.selectedCategories.map((category) => encodeURIComponent(category)).join(',');
            qs += `&categories=${urlEncodedCategories}&`;
        }
        if (isDefined(options.selectedTags) && options.selectedTags.length > 0) {
            const urlEncodedTags = options.selectedTags.map((tag) => encodeURIComponent(tag)).join(',');
            qs += `&tags=${urlEncodedTags}`;
        }
    }
    return `https://image.thum.io/get/auth/71951-map.proxi.co/width/2000/crop/1050/noanimate/wait/30/${target}?topic_hash=${hash}${qs}`;
}

export function getSmallSocialImageForTopic(topic: GetTopicResponse | TopicSearchResult, options?: MapSetUpOptions): string {
    const target = thumioTargetForTopic(topic);
    if (target === null) {
        return '/images/share.png';
    }
    const hash = calculateScreenshotHash(topic);
    let qs = '';
    if (options) {
        if (isDefined(options.center_lat)) {
            qs += `&lat=${options.center_lat}`;
        }
        if (isDefined(options.center_lng)) {
            qs += `&lng=${options.center_lng}`;
        }
        if (isDefined(options.zoom)) {
            qs += `&zoom=${options.zoom}`;
        }
        if (isDefined(options.selectedCategories) && options.selectedCategories.length > 0) {
            const urlEncodedCategories = options.selectedCategories.map((category) => encodeURIComponent(category)).join(',');
            qs += `&categories=${urlEncodedCategories}`;
        }
        if (isDefined(options.selectedTags) && options.selectedTags.length > 0) {
            const urlEncodedTags = options.selectedTags.map((tag) => encodeURIComponent(tag)).join(',');
            qs += `&tags=${urlEncodedTags}`;
        }
    }
    // aligned with the server-side `build_thumio_screenshot_url` so both sides share Thum.io's cache.
    // No `crop`: Thum.io crops against its full render before the width downscale, so
    // `width/300/crop/157` produced a 300×39 strip — render natural-aspect at 600px instead.
    return `https://image.thum.io/get/auth/71951-map.proxi.co/width/600/noanimate/allowJPG/wait/10/${target}?topic_hash=${hash}${qs}`;
}

export async function tinyLatitudeMove(topic: GetTopicResponse): Promise<GetTopicResponse> {
    const new_topic = createTopicRequestFromTopic(topic);
    // get the last three digits of the lat and add 001 to it
    const lat = new_topic.default_map_center?.latLng.lat;
    if (lat !== undefined && isDefined(new_topic.default_map_center)) {
        // get the last three digits of the lat and add 001 to it
        const lat_str = lat.toString();
        const last_three = lat_str.slice(-3);
        const new_last_three = (parseInt(last_three) + 1).toString().padStart(3, '0');
        const new_lat_str = lat_str.slice(0, -3) + new_last_three;
        new_topic.default_map_center.latLng.lat = parseFloat(new_lat_str) ?? lat;
        const updated_topic = await topicData.fetchAndUpdateTopicData(new_topic, 'small_social_image');
        return updated_topic;
    }
    return topic;
}

export function escapeCsvValue(value: string | undefined | null): string {
    if (value === undefined || value === null) {
        return '';
    }
    if (value.includes(',') || value.includes('\n') || value.includes('"')) {
        return `"${value.replace(/"/g, '""')}"`;
    } else {
        return value;
    }
}

export const renderProxiLogo = html`
    ${!detectMobileViewport() ? html`
        <a id="logoDesktop" class="desktopOnly appLogo">
            <img src="/images/logo_simple.png" style="max-height: 2.5rem; vertical-align: middle;" />
        </a>
    ` : html`
        <a id="logoMobile" class="mobileOnly appLogo">
            <img src="/images/logo_simple.png" style="max-height: 1.5rem; vertical-align: middle;" />
        </a>
    `}
`;

export function renderOrganizationLogo(organizationLogo: string | undefined): HTMLTemplateResult {
    if (organizationLogo === undefined || organizationLogo === null || organizationLogo === '') {
        return renderProxiLogo;
    }
    if (!detectMobileViewport()) {
    return html`
        <a id="logoDesktop" class="desktopOnly appLogo">
            <img src=${String(organizationLogo)} style="max-height: 2.5rem; vertical-align: middle;" />
        </a>
    `;

    }

    return html`
        <a id="logoMobile" class="mobileOnly appLogo">
            <img src=${String(organizationLogo)} style="max-height: 1.5rem; vertical-align: middle;" />
        </a>
    `;
}
